import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import {
  CheckCircle, XCircle, Clock, AlertTriangle,
  RefreshCw, ChevronLeft, ChevronRight, ShieldAlert, X,
} from 'lucide-react';
import {
  getFlaggedRegistrations,
  approveFlaggedRegistration,
  rejectFlaggedRegistration,
  FlaggedRegistration,
  FlaggedRegistrationsFilters,
  FraudSignals,
} from '../../services/fraudApi';

type StatusFilter = 'all' | 'pending' | 'approved' | 'rejected';

interface ConfirmDialog {
  type: 'approve' | 'reject';
  item: FlaggedRegistration;
}

interface Filters {
  startDate: string;
  endDate: string;
  minScore: string;
  maxScore: string;
}

const STATUS_TABS: { label: string; value: StatusFilter }[] = [
  { label: 'All', value: 'all' },
  { label: 'Pending', value: 'pending' },
  { label: 'Approved', value: 'approved' },
  { label: 'Rejected', value: 'rejected' },
];

const SIGNAL_LABELS: Record<keyof FraudSignals, string> = {
  disposableEmail: 'Disposable Email',
  ipReputationLow: 'Low IP Rep',
  proxyVpnTor: 'Proxy/VPN/TOR',
  deviceFingerprintAbuse: 'Device Abuse',
  captchaWeak: 'Weak CAPTCHA',
  rateLimitExceeded: 'Rate Limited',
};

const SIGNAL_COLORS: Record<keyof FraudSignals, string> = {
  disposableEmail: 'bg-orange-100 text-orange-700 border-orange-200',
  ipReputationLow: 'bg-red-100 text-red-700 border-red-200',
  proxyVpnTor: 'bg-purple-100 text-purple-700 border-purple-200',
  deviceFingerprintAbuse: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  captchaWeak: 'bg-blue-100 text-blue-700 border-blue-200',
  rateLimitExceeded: 'bg-pink-100 text-pink-700 border-pink-200',
};

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function getScoreColor(score: number): string {
  if (score >= 70) return 'text-danger-700 bg-danger-100';
  if (score >= 40) return 'text-warning-700 bg-warning-100';
  return 'text-success-700 bg-success-100';
}

function SignalBadges({ signals }: { signals: FraudSignals }) {
  const active = (Object.keys(signals) as (keyof FraudSignals)[]).filter(k => signals[k]);
  if (active.length === 0) return <span className="text-xs text-secondary-300 italic">None</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {active.map(key => (
        <span
          key={key}
          className={`inline-block px-1.5 py-0.5 text-xs font-medium rounded border ${SIGNAL_COLORS[key]}`}
        >
          {SIGNAL_LABELS[key]}
        </span>
      ))}
    </div>
  );
}

const AdminFraudPanel = () => {
  const [items, setItems] = useState<FlaggedRegistration[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const limit = 20;
  const totalPages = Math.max(1, Math.ceil(total / limit));

  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [filters, setFilters] = useState<Filters>({ startDate: '', endDate: '', minScore: '', maxScore: '' });
  const [toast, setToast] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [confirm, setConfirm] = useState<ConfirmDialog | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  const showToast = (type: 'success' | 'error', text: string) => {
    setToast({ type, text });
    setTimeout(() => setToast(null), 4000);
  };

  const fetchItems = useCallback(async (p: number, status: StatusFilter, f: Filters) => {
    try {
      setLoading(true);
      setError(null);
      const params: FlaggedRegistrationsFilters = { page: p, limit };
      if (status !== 'all') params.status = status;
      if (f.startDate) params.startDate = f.startDate;
      if (f.endDate) params.endDate = f.endDate;
      if (f.minScore !== '') params.minScore = Number(f.minScore);
      if (f.maxScore !== '') params.maxScore = Number(f.maxScore);
      const res = await getFlaggedRegistrations(params);
      setItems(res.items);
      setTotal(res.total);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load flagged registrations.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    setPage(1);
    fetchItems(1, statusFilter, filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const handleApplyFilters = () => {
    setPage(1);
    fetchItems(1, statusFilter, filters);
  };

  const handleClearFilters = () => {
    const cleared: Filters = { startDate: '', endDate: '', minScore: '', maxScore: '' };
    setFilters(cleared);
    setPage(1);
    fetchItems(1, statusFilter, cleared);
  };

  const handleApprove = async () => {
    if (!confirm || confirm.type !== 'approve') return;
    setActionLoading(true);
    try {
      await approveFlaggedRegistration(confirm.item.id);
      showToast('success', `Registration approved for ${confirm.item.user?.email ?? 'user'}`);
      fetchItems(page, statusFilter, filters);
    } catch (err: unknown) {
      showToast('error', err instanceof Error ? err.message : 'Failed to approve.');
    } finally {
      setActionLoading(false);
      setConfirm(null);
    }
  };

  const handleReject = async () => {
    if (!confirm || confirm.type !== 'reject') return;
    setActionLoading(true);
    try {
      await rejectFlaggedRegistration(confirm.item.id);
      showToast('success', `Registration rejected for ${confirm.item.user?.email ?? 'user'}`);
      fetchItems(page, statusFilter, filters);
    } catch (err: unknown) {
      showToast('error', err instanceof Error ? err.message : 'Failed to reject.');
    } finally {
      setActionLoading(false);
      setConfirm(null);
    }
  };

  const pendingCount = items.filter(i => i.status === 'pending').length;
  const approvedCount = items.filter(i => i.status === 'approved').length;
  const rejectedCount = items.filter(i => i.status === 'rejected').length;
  const hasActiveFilters = filters.startDate || filters.endDate || filters.minScore !== '' || filters.maxScore !== '';

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto space-y-4 md:space-y-6">

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-secondary-900">Fraud Detection</h1>
          <p className="text-secondary-500 text-sm mt-1">Review and action flagged registration attempts</p>
        </div>
        <button
          onClick={() => fetchItems(page, statusFilter, filters)}
          className="flex items-center gap-2 px-3 py-2 text-sm text-secondary-600 border border-secondary-200 rounded-lg hover:bg-secondary-50 transition-colors self-start sm:self-auto"
        >
          <RefreshCw className="h-4 w-4" /> Refresh
        </button>
      </div>

      {/* Toast */}
      {toast && (
        <div className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium border ${
          toast.type === 'success'
            ? 'bg-success-50 text-success-800 border-success-200'
            : 'bg-danger-50 text-danger-800 border-danger-200'
        }`}>
          {toast.type === 'success' ? <CheckCircle className="h-4 w-4 shrink-0" /> : <XCircle className="h-4 w-4 shrink-0" />}
          {toast.text}
        </div>
      )}

      {/* Stats row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Total Flagged', value: total, icon: ShieldAlert, color: 'from-secondary-500 to-secondary-600' },
          { label: 'Pending Review', value: pendingCount, icon: Clock, color: 'from-warning-500 to-warning-600' },
          { label: 'Approved', value: approvedCount, icon: CheckCircle, color: 'from-success-500 to-success-600' },
          { label: 'Rejected', value: rejectedCount, icon: XCircle, color: 'from-danger-500 to-danger-600' },
        ].map(({ label, value, icon: Icon, color }) => (
          <div key={label} className="stats-card">
            <div className="flex items-center justify-between mb-3">
              <div className={`w-10 h-10 bg-gradient-to-r ${color} rounded-xl flex items-center justify-center`}>
                <Icon className="h-5 w-5 text-white" />
              </div>
            </div>
            <div className="stats-value">{value}</div>
            <div className="stats-label">{label}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="space-y-3">
        {/* Status tabs */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex gap-1 bg-secondary-100 p-1 rounded-lg overflow-x-auto">
            {STATUS_TABS.map(({ label, value }) => (
              <button
                key={value}
                onClick={() => setStatusFilter(value)}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors whitespace-nowrap ${
                  statusFilter === value
                    ? 'bg-white text-secondary-900 shadow-sm'
                    : 'text-secondary-600 hover:text-secondary-900'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          {!loading && (
            <span className="text-sm text-secondary-500 sm:ml-auto whitespace-nowrap">
              {total} total
            </span>
          )}
        </div>

        {/* Date + score filters */}
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1">
            <label className="text-xs text-secondary-500 font-medium">Start Date</label>
            <input
              type="date"
              value={filters.startDate}
              onChange={e => setFilters(f => ({ ...f, startDate: e.target.value }))}
              className="px-3 py-1.5 text-sm border border-secondary-200 rounded-lg bg-white focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs text-secondary-500 font-medium">End Date</label>
            <input
              type="date"
              value={filters.endDate}
              onChange={e => setFilters(f => ({ ...f, endDate: e.target.value }))}
              className="px-3 py-1.5 text-sm border border-secondary-200 rounded-lg bg-white focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs text-secondary-500 font-medium">Min Score</label>
            <input
              type="number"
              min={0} max={100}
              placeholder="0"
              value={filters.minScore}
              onChange={e => setFilters(f => ({ ...f, minScore: e.target.value }))}
              className="w-24 px-3 py-1.5 text-sm border border-secondary-200 rounded-lg bg-white focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs text-secondary-500 font-medium">Max Score</label>
            <input
              type="number"
              min={0} max={100}
              placeholder="100"
              value={filters.maxScore}
              onChange={e => setFilters(f => ({ ...f, maxScore: e.target.value }))}
              className="w-24 px-3 py-1.5 text-sm border border-secondary-200 rounded-lg bg-white focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <button
            onClick={handleApplyFilters}
            className="px-4 py-1.5 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors"
          >
            Apply
          </button>
          {hasActiveFilters && (
            <button
              onClick={handleClearFilters}
              className="px-3 py-1.5 text-sm text-secondary-600 border border-secondary-200 rounded-lg hover:bg-secondary-50 transition-colors"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex justify-center py-16">
          <div className="text-center">
            <LoadingSpinner size="xl" />
            <p className="mt-4 text-secondary-500 text-sm">Loading flagged registrations...</p>
          </div>
        </div>
      )}

      {/* Error */}
      {!loading && error && (
        <div className="text-center py-12">
          <AlertTriangle className="h-10 w-10 text-danger-400 mx-auto mb-3" />
          <p className="font-medium text-secondary-900">Failed to load data</p>
          <p className="text-sm text-secondary-500 mt-1">{error}</p>
          <Button variant="secondary" size="sm" className="mt-4"
            onClick={() => fetchItems(page, statusFilter, filters)}>
            Retry
          </Button>
        </div>
      )}

      {/* Empty */}
      {!loading && !error && items.length === 0 && (
        <div className="text-center py-16">
          <div className="text-5xl mb-4">🛡️</div>
          <p className="font-medium text-secondary-700">No flagged registrations found</p>
          <p className="text-sm text-secondary-400 mt-1">
            {statusFilter === 'all' ? 'No flagged attempts yet.' : `No ${statusFilter} registrations.`}
          </p>
        </div>
      )}

      {/* Table */}
      {!loading && !error && items.length > 0 && (
        <Card variant="default" padding="none">
          <CardContent className="p-0">

            {/* Desktop table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-secondary-50 border-b border-secondary-200">
                  <tr>
                    {['Timestamp', 'Email', 'IP Address', 'Fraud Score', 'Signals', 'Status', 'Actions'].map(h => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-secondary-500 uppercase tracking-wider whitespace-nowrap">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-secondary-100 bg-white">
                  {items.map((item) => (
                    <tr key={item.id} className="hover:bg-secondary-50 transition-colors">

                      <td className="px-4 py-3 whitespace-nowrap text-xs text-secondary-400">
                        {formatDate(item.createdAt)}
                      </td>

                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="text-secondary-800 font-medium">
                          {item.user?.email ?? '—'}
                        </span>
                      </td>

                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="font-mono text-xs text-secondary-600">
                          {item.signupAttempt?.ipAddress ?? '—'}
                        </span>
                      </td>

                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-bold ${getScoreColor(item.fraudScore)}`}>
                          {item.fraudScore}
                        </span>
                      </td>

                      <td className="px-4 py-3 max-w-xs">
                        <SignalBadges signals={item.signals} />
                      </td>

                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                          item.status === 'pending' ? 'bg-warning-100 text-warning-700' :
                          item.status === 'approved' ? 'bg-success-100 text-success-700' :
                          'bg-danger-100 text-danger-700'
                        }`}>
                          {item.status === 'pending' && <Clock className="h-3.5 w-3.5" />}
                          {item.status === 'approved' && <CheckCircle className="h-3.5 w-3.5" />}
                          {item.status === 'rejected' && <XCircle className="h-3.5 w-3.5" />}
                          {item.status.charAt(0).toUpperCase() + item.status.slice(1)}
                        </span>
                      </td>

                      <td className="px-4 py-3 whitespace-nowrap text-right">
                        {item.status === 'pending' ? (
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => setConfirm({ type: 'approve', item })}
                              title="Approve registration"
                              className="p-1.5 text-success-600 bg-success-50 border border-success-200 rounded-lg hover:bg-success-100 transition-colors"
                            >
                              <CheckCircle className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => setConfirm({ type: 'reject', item })}
                              title="Reject registration"
                              className="p-1.5 text-danger-600 bg-danger-50 border border-danger-200 rounded-lg hover:bg-danger-100 transition-colors"
                            >
                              <XCircle className="h-4 w-4" />
                            </button>
                          </div>
                        ) : (
                          <span className="text-xs text-secondary-300">—</span>
                        )}
                      </td>

                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile card list */}
            <div className="md:hidden divide-y divide-secondary-100">
              {items.map((item) => (
                <div key={item.id} className="p-4 space-y-3">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="font-medium text-secondary-900 text-sm">{item.user?.email ?? '—'}</div>
                      <div className="text-xs text-secondary-400 font-mono">{item.signupAttempt?.ipAddress ?? '—'}</div>
                    </div>
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium shrink-0 ${
                      item.status === 'pending' ? 'bg-warning-100 text-warning-700' :
                      item.status === 'approved' ? 'bg-success-100 text-success-700' :
                      'bg-danger-100 text-danger-700'
                    }`}>
                      {item.status === 'pending' && <Clock className="h-3.5 w-3.5" />}
                      {item.status === 'approved' && <CheckCircle className="h-3.5 w-3.5" />}
                      {item.status === 'rejected' && <XCircle className="h-3.5 w-3.5" />}
                      {item.status.charAt(0).toUpperCase() + item.status.slice(1)}
                    </span>
                  </div>

                  <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
                    <div>
                      <span className="text-secondary-400">Fraud Score</span>
                      <div>
                        <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-bold ${getScoreColor(item.fraudScore)}`}>
                          {item.fraudScore}
                        </span>
                      </div>
                    </div>
                    <div>
                      <span className="text-secondary-400">Timestamp</span>
                      <div className="text-secondary-600">{formatDate(item.createdAt)}</div>
                    </div>
                    <div className="col-span-2">
                      <span className="text-secondary-400">Signals</span>
                      <div className="mt-1">
                        <SignalBadges signals={item.signals} />
                      </div>
                    </div>
                  </div>

                  {item.status === 'pending' && (
                    <div className="flex items-center gap-2 pt-1">
                      <button
                        onClick={() => setConfirm({ type: 'approve', item })}
                        className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-success-700 bg-success-50 border border-success-200 rounded-lg hover:bg-success-100 transition-colors"
                      >
                        <CheckCircle className="h-3.5 w-3.5" /> Approve
                      </button>
                      <button
                        onClick={() => setConfirm({ type: 'reject', item })}
                        className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-danger-700 bg-danger-50 border border-danger-200 rounded-lg hover:bg-danger-100 transition-colors"
                      >
                        <XCircle className="h-3.5 w-3.5" /> Reject
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>

          </CardContent>
        </Card>
      )}

      {/* Pagination */}
      {!loading && !error && totalPages > 1 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
          <span className="text-sm text-secondary-600 order-2 sm:order-none">
            Page {page} of {totalPages}
          </span>
          <div className="flex items-center gap-2 w-full sm:w-auto justify-between sm:justify-end">
            <button
              disabled={page <= 1}
              onClick={() => { setPage(p => p - 1); fetchItems(page - 1, statusFilter, filters); }}
              className="flex items-center gap-1 px-3 py-2 text-sm border border-secondary-200 rounded-lg hover:bg-secondary-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft className="h-4 w-4" /> Previous
            </button>
            <button
              disabled={page >= totalPages}
              onClick={() => { setPage(p => p + 1); fetchItems(page + 1, statusFilter, filters); }}
              className="flex items-center gap-1 px-3 py-2 text-sm border border-secondary-200 rounded-lg hover:bg-secondary-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Next <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Confirm Dialog */}
      {confirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md mx-4 p-6">
            <div className="flex items-start justify-between mb-4">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                confirm.type === 'approve' ? 'bg-success-100' : 'bg-danger-100'
              }`}>
                {confirm.type === 'approve'
                  ? <CheckCircle className="h-5 w-5 text-success-600" />
                  : <XCircle className="h-5 w-5 text-danger-600" />}
              </div>
              <button onClick={() => setConfirm(null)} className="text-secondary-400 hover:text-secondary-600">
                <X className="h-5 w-5" />
              </button>
            </div>

            <h3 className="text-lg font-semibold text-secondary-900 mb-1">
              {confirm.type === 'approve' ? 'Approve Registration' : 'Reject Registration'}
            </h3>
            <p className="text-sm text-secondary-500 mb-4">
              {confirm.type === 'approve'
                ? `This will approve and activate the account for ${confirm.item.user?.email ?? 'this user'}.`
                : `This will permanently reject the registration for ${confirm.item.user?.email ?? 'this user'}.`}
            </p>

            <div className="bg-secondary-50 rounded-xl p-3 text-sm space-y-1 mb-6">
              <div className="flex justify-between">
                <span className="text-secondary-500">Email</span>
                <span className="font-medium text-secondary-900">{confirm.item.user?.email ?? '—'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-secondary-500">IP Address</span>
                <span className="font-mono text-xs text-secondary-700">{confirm.item.signupAttempt?.ipAddress ?? '—'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-secondary-500">Fraud Score</span>
                <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${getScoreColor(confirm.item.fraudScore)}`}>
                  {confirm.item.fraudScore}
                </span>
              </div>
              <div className="flex justify-between items-start gap-2">
                <span className="text-secondary-500 shrink-0">Signals</span>
                <div className="text-right">
                  <SignalBadges signals={confirm.item.signals} />
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setConfirm(null)}
                className="flex-1 px-4 py-2 text-sm font-medium text-secondary-700 border border-secondary-200 rounded-lg hover:bg-secondary-50 transition-colors"
              >
                Cancel
              </button>
              <button
                disabled={actionLoading}
                onClick={confirm.type === 'approve' ? handleApprove : handleReject}
                className={`flex-1 px-4 py-2 text-sm font-semibold text-white rounded-lg transition-colors disabled:opacity-60 ${
                  confirm.type === 'approve'
                    ? 'bg-success-600 hover:bg-success-700'
                    : 'bg-danger-600 hover:bg-danger-700'
                }`}
              >
                {actionLoading ? 'Processing...' : confirm.type === 'approve' ? 'Approve' : 'Reject'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default AdminFraudPanel;
